import type { MarketFilters } from "@/shared/types/bondFilters";
import { DEFAULT_MARKET_FILTERS } from "@/shared/types/bondFilters";
import type { MessageFilters } from "@/shared/types/message";
import { DEFAULT_MESSAGE_FILTERS } from "@/shared/types/message";
import { storageGet, storageSet } from "@/shared/utils/storage";

/**
 * Filter persistence for the Markets page.
 *
 * Markets (Quotes) and RFQs share ONE canonical `MarketFilters` object so a
 * filter set on one tab carries to the other. Tab-specific filters that don't
 * apply to the active tab are masked out of the URL/results (see
 * `marketFilterScope`) but stay in this canonical object so they are restored
 * when the user returns to a tab where they apply.
 *
 * Messages keep their own snapshot because they use a different filter shape.
 *
 * On initial load the URL takes priority: this store is only consulted when the
 * URL carries no filter params.
 */

const STORE_VERSION = 3;

const MARKET_KEY_PREFIX = "tower.markets.filters";
const CANONICAL_KEY = MARKET_KEY_PREFIX;
const MESSAGE_KEY = `${MARKET_KEY_PREFIX}.messages`;

export function saveMarketFilters(filters: MarketFilters): void {
  storageSet(CANONICAL_KEY, filters, { version: STORE_VERSION });
}

export function loadMarketFilters(): MarketFilters {
  const stored = storageGet<MarketFilters>(CANONICAL_KEY, STORE_VERSION);
  if (!stored) return { ...DEFAULT_MARKET_FILTERS };
  return { ...stored };
}

export function saveMessageTabFilters(filters: MessageFilters): void {
  storageSet(MESSAGE_KEY, filters, { version: STORE_VERSION });
}

export function loadMessageTabFilters(): MessageFilters {
  const stored = storageGet<MessageFilters>(MESSAGE_KEY, STORE_VERSION);
  if (!stored) return { ...DEFAULT_MESSAGE_FILTERS };
  return { ...stored };
}
